/**
 * ASSISTANT — UN POINT DE VENTE À LA FOIS.
 *
 * Le parcours ne présente jamais un formulaire géant : une étape, une caisse,
 * quatre questions dans l'ordre où on se les pose à 6 h du matin.
 *
 *   1. ce que la SOURCE annonce — le fait extérieur, jamais réécrit ;
 *   2. les TPE disponibles — sans imposer une correspondance caisse ↔ terminal
 *      qui serait fausse : un terminal CIRCULE au cours de la nuit ;
 *   3. le COMPTAGE des espèces, coupure par coupure, total en direct ;
 *   4. le RAPPROCHEMENT : source, constaté, retenu, écart.
 *
 * ═══ L'ÉCART N'EST PAS UNE FAUTE ═══
 * Un écart carte important n'est pas une alerte critique : c'est souvent la
 * réalité d'une nuit chargée, terminaux compris. L'écran le dit, et ne
 * transforme jamais un écart en note portée sur une personne.
 *
 * ═══ LE SEUIL NE DISPENSE PAS DU MOTIF ═══
 * La couleur d'un écart dépend de seuils configurables. Retenir un montant
 * différent du constaté exige une explication d'au moins douze caractères,
 * quelle que soit la taille de l'écart — règle portée par la base, revérifiée
 * par l'API, et rappelée ici.
 *
 * ═══ LE RAPPORT D'UN CÔTÉ, CE QU'ON A COMPTÉ DE L'AUTRE (07/10/2026) ═══
 * « Dans la clôture d'un bar, il faudrait bien distinguer les totaux du
 * rapport et de ce qu'on a rentré, au niveau des cases au début. » Les cases
 * du haut ne montraient que le rapport. Elles sont désormais TROIS groupes
 * côte à côte, lignes alignées : ce que le rapport annonce, ce que vous avez
 * compté, et l'écart — moyen par moyen et au total, hors offerts, sur les
 * seules lignes comptées. Ils suivent la saisie en direct (tickets TPE,
 * comptage, rapprochement), avec les règles du rail et du bandeau
 * (`chiffresPointDeVente`, closing-rail-model).
 *
 * ═══ L'ACCUEIL PORTE LES ENTRÉES ═══
 * La guestlist se recopie depuis la feuille du guichet : à l'étape de
 * l'Accueil (la billetterie), avec le récapitulatif des billets Shotgun
 * (`EntreesAccueil`, entrees.tsx).
 *
 * ═══ L'ACCUEIL ET LES VENTES D'UNE AUTRE NUIT (08/10/2026) ═══
 * La caisse Shotgun restée ouverte sur une ancienne soirée : l'écart de
 * l'Accueil (+1 059 € le 03/10) vient de là. Les cases du haut DISENT d'où
 * il vient (« dont 1 060 € vendus cette nuit sur la soirée du 26/09 »), le
 * panneau juste dessous (`GuichetHorsNuit`) dit ce que rattacher changera
 * (« attendu 4 460 €, écart −1 € ») et le fait d'un clic. Rattachées, ces
 * ventes entrent dans l'ANNONCÉ de l'Accueil, qui devient l'attendu :
 * « guichet 3 400 € + 1 060 € rattachés = attendu 4 460 € ». La répartition
 * TPE / espèces, elle, reste ce qu'elle est : rien ne la maquille.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { Banknote, Calculator, CreditCard, FileText, Gift, Plus, Scale, Trash2, X } from 'lucide-react'
import { Combobox, } from '../admin/pickers.js'
import { Modal, Select } from '../admin/primitives.js'
import { formatEcart, formatMoney } from './money.js'
import { PosDetail } from './PosDetail.js'
import { EntreesAccueil } from './entrees.js'
import { GuichetHorsNuit } from './GuichetHorsNuit.js'
import { jourMois } from './nights-common.js'
import { attenduDe, chiffresPointDeVente, niveauEcart, type ChiffresMoyen, type ChiffresPointDeVente, type NiveauEcart } from './closing-rail-model.js'
import {
  Ecart, MOYEN_CARTE, MOYEN_ESPECES, MOYEN_OFFERT, MoneyInput, Montant, MotifField,
  enCentimes, lireJson, motifSuffisant,
  type ClosingApi, type ClosingLineView, type ClosingPosView, type TpeTicketView,
  type PaymentMethodRow, type TerminalRow,
} from './ClosingCommon.js'

export function ClosingStepPos(props: {
  api: ClosingApi
  pos: ClosingPosView
  terminaux: TerminalRow[]
  moyens: PaymentMethodRow[]
  figee: boolean
  rang: number
  total: number
}) {
  const { api, pos, terminaux, moyens, figee, rang, total } = props
  const editable = api.peut('finance.closure.edit') && !figee
  const peutRetenir = api.peut('finance.amount.override') && !figee
  const seuils = { warn: api.settings.discrepancy_warn_cents, alert: api.settings.discrepancy_alert_cents }

  /* ═════════════ comptage des espèces ═════════════ */

  const coupures = useMemo(
    () => [...api.settings.denominations_cents].sort((a, b) => b - a),
    [api.settings.denominations_cents],
  )

  const [quantites, setQuantites] = useState<Record<string, string>>(() => {
    const q: Record<string, string> = {}
    for (const c of pos.cash_counts) q[String(c.denomination_cents)] = String(c.quantity)
    return q
  })

  const totalCompte = useMemo(
    () => coupures.reduce((t, d) => {
      const n = Number.parseInt(quantites[String(d)] ?? '', 10)
      return t + (Number.isSafeInteger(n) && n > 0 ? d * n : 0)
    }, 0),
    [coupures, quantites],
  )

  /* Enregistrement différé : on n'écrit pas une fois par frappe, mais on
     n'attend pas non plus un bouton — une saisie perdue, c'est un recomptage. */
  const premierRendu = useRef(true)
  useEffect(() => {
    if (premierRendu.current) { premierRendu.current = false; return }
    if (!editable) return
    const t = window.setTimeout(() => {
      const counts = coupures
        .map((d) => ({ denomination_cents: d, quantity: Number.parseInt(quantites[String(d)] ?? '', 10) }))
        .filter((c) => Number.isSafeInteger(c.quantity) && c.quantity > 0)
      void api.ecrire('PUT', `/api/v1/finance/closings/${api.closing.id}/cash/${pos.id}`, { counts })
    }, 900)
    return () => window.clearTimeout(t)
  }, [quantites]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Saisie directe du total : autorisée, mais expliquée (elle court-circuite
     la seule vérification arithmétique dont on dispose). */
  const [directOuvert, setDirectOuvert] = useState(false)
  const [directCents, setDirectCents] = useState<number | null>(null)
  const [directMotif, setDirectMotif] = useState('')
  const overrideActif = enCentimes(pos.cash_total_override_cents) !== null

  const poserTotalDirect = async () => {
    if (directCents === null || !motifSuffisant(directMotif)) return
    const ok = await api.ecrire(
      'POST', `/api/v1/finance/closings/${api.closing.id}/cash/${pos.id}/override`,
      { total_cents: directCents, reason: directMotif.trim() },
    )
    if (ok) { setDirectOuvert(false); setDirectMotif(''); setDirectCents(null) }
  }

  /* `total_cents: null` rend la main au comptage par coupures — et efface le
     motif avec lui : un motif orphelin expliquerait un forçage disparu. */
  const reprendreComptage = async () => {
    await api.ecrire(
      'POST', `/api/v1/finance/closings/${api.closing.id}/cash/${pos.id}/override`,
      { total_cents: null },
    )
  }

  /* ═════════════ rapprochement ═════════════ */

  const [observes, setObserves] = useState<Record<string, number | null>>(() => {
    const o: Record<string, number | null> = {}
    for (const l of pos.lines) o[l.id] = enCentimes(l.observed_cents)
    return o
  })
  /* Le serveur fait foi pour une ligne INTACTE ; une saisie en cours (qui
     diffère de ce que le serveur disait au dernier passage) est gardée. Garder
     toutes les valeurs locales figeait une ligne que les tickets TPE ou un
     autre poste venaient de changer — et les cases du haut, qui lisent ces
     valeurs en direct, l'auraient montrée périmée. */
  const serveurAvant = useRef<Record<string, number | null>>(
    Object.fromEntries(pos.lines.map((l) => [l.id, enCentimes(l.observed_cents)])),
  )
  useEffect(() => {
    const precedent = serveurAvant.current
    serveurAvant.current = Object.fromEntries(pos.lines.map((l) => [l.id, enCentimes(l.observed_cents)]))
    setObserves((avant) => {
      const apres: Record<string, number | null> = {}
      for (const l of pos.lines) {
        const enCours = l.id in avant && (avant[l.id] ?? null) !== (precedent[l.id] ?? null)
        apres[l.id] = enCours ? avant[l.id] ?? null : enCentimes(l.observed_cents)
      }
      return apres
    })
  }, [pos.lines])

  /* ═════════════ ce qui est en train d'être saisi, pour les cases du haut ═════════════ */

  /* La somme des tickets TPE À L'ÉCRAN (le composant des tickets la remonte à
     chaque frappe) ; `null` = aucun ticket chiffré. */
  const ticketsServeur = pos.tpe_tickets.length > 0 ? pos.tpe_tickets.reduce((t, x) => t + x.amount_cents, 0) : null
  const [ticketsDirect, setTicketsDirect] = useState<number | null>(ticketsServeur)
  const [nbTickets, setNbTickets] = useState(pos.tpe_tickets.length)
  const surTickets = useCallback((cents: number | null, n: number) => { setTicketsDirect(cents); setNbTickets(n) }, [])

  /* Le comptage des espèces À L'ÉCRAN : le total saisi directement s'il fait
     foi, sinon les coupures tapées ; `null` tant qu'aucune n'est remplie. */
  const aDesCoupures = coupures.some((d) => {
    const n = Number.parseInt(quantites[String(d)] ?? '', 10)
    return Number.isSafeInteger(n) && n > 0
  })
  const comptageDirect = overrideActif ? enCentimes(pos.cash_total_override_cents) : aDesCoupures ? totalCompte : null

  const chiffres = useMemo(
    () => chiffresPointDeVente(pos.lines, { constates: observes, tickets: ticketsDirect, comptage: comptageDirect }),
    [pos.lines, observes, ticketsDirect, comptageDirect],
  )

  const enregistrerConstate = async (l: ClosingLineView) => {
    const v = observes[l.id] ?? null
    if (v === enCentimes(l.observed_cents)) return
    await api.ecrire('PUT', `/api/v1/finance/closings/${api.closing.id}/lines`, {
      lines: [{ id: l.id, payment_method: l.payment_method, observed_cents: v }],
    })
  }


  /* Retenir un autre montant : la seule écriture qui exige un motif ET une
     ré-authentification forte (portée par `adminMutate`). */
  const [retenue, setRetenue] = useState<{ ligne: ClosingLineView; cents: number | null; motif: string } | null>(null)

  const poserRetenue = async () => {
    if (!retenue || retenue.cents === null || !motifSuffisant(retenue.motif)) return
    const ok = await api.ecrire(
      'POST', `/api/v1/finance/closings/${api.closing.id}/lines/${retenue.ligne.id}/retain`,
      { retained_cents: retenue.cents, reason: retenue.motif.trim() },
    )
    if (ok) setRetenue(null)
  }

  /* Ajouter une ligne pour un moyen de paiement non encore présent. */
  const [nouveau, setNouveau] = useState('')
  const moyensLibres = useMemo(
    () => moyens.filter((m) => m.active && !pos.lines.some((l) => l.payment_method === m.code)),
    [moyens, pos.lines],
  )
  const ajouterLigne = async () => {
    if (!nouveau) return
    const ok = await api.ecrire('PUT', `/api/v1/finance/closings/${api.closing.id}/lines`, {
      lines: [{ closing_pos_id: pos.id, payment_method: nouveau }],
    })
    if (ok) setNouveau('')
  }

  /* ═════════════ ce que la source annonce ═════════════ */

  const srcCarte = chiffres.moyens.find((m) => m.code === MOYEN_CARTE)?.annonce ?? null
  const aUneSource = pos.lines.some((l) => enCentimes(attenduDe(l)) !== null)
  const origine = pos.point_of_sale_kind === 'BILLETTERIE' ? 'guichet Shotgun' : 'rapport ZAP'

  /* Les ventes du guichet d'une autre nuit qui touchent CET Accueil — et la
     phrase qui relie l'écart affiché à sa cause, sous les cases du haut. */
  const estAccueil = api.closing.off_night?.accueil_pos_id != null
    ? api.closing.off_night.accueil_pos_id === pos.id
    : pos.point_of_sale_kind === 'BILLETTERIE'
  const casHorsNuit = estAccueil ? (api.closing.off_night?.cases ?? []) : []
  const explicationHorsNuit = casHorsNuit
    .filter((c) => c.status === 'EN_ATTENTE' && (c.total_cents ?? 0) > 0)
    .map((c) => c.side === 'VENTE'
      ? `Dont ${formatMoney(c.total_cents)} vendus cette nuit sur la soirée du ${jourMois(c.from_business_date)}, en attente de décision : voir juste en dessous.`
      : c.source_contains
        ? c.source_contains_cents != null && c.card_present_cents !== null && c.cash_cents !== null
          && c.source_contains_cents < c.card_present_cents + c.cash_cents
          ? `La source contient ${formatMoney(c.source_contains_cents)} des ${formatMoney(c.total_cents)} vendus pendant la nuit du ${jourMois(c.sale_business_date)} (ceux vendus avant sa pose), en attente de décision : voir juste en dessous.`
          : `La source contient ${formatMoney(c.total_cents)} vendus pendant la nuit du ${jourMois(c.sale_business_date)}, en attente de décision : voir juste en dessous.`
        : `${formatMoney(c.total_cents)} de cette soirée vendus pendant la nuit du ${jourMois(c.sale_business_date)} attendent une décision : voir juste en dessous.`)
  const ligneEspeces = pos.lines.find((l) => l.payment_method === MOYEN_ESPECES && enCentimes(l.source_cents) !== null)
    ?? pos.lines.find((l) => l.payment_method === MOYEN_ESPECES) ?? null
  const especesEffectives = enCentimes(pos.cash_effective_cents)
  /* Les lignes du rapprochement : l'argent qui se compte d'abord, les offerts
     (une information, rien à compter) à part. */
  const lignesComptees = pos.lines.filter((l) => l.payment_method !== MOYEN_OFFERT)
  const lignesOffertes = pos.lines.filter((l) => l.payment_method === MOYEN_OFFERT)

  /* Reporter le comptage sur la ligne Espèces — PROPOSÉ, jamais fait dans le
     dos : c'est une décision (le retenu de la ligne en dépend). Deux boutons
     y mènent : la case du haut et l'encart sous le rapprochement.

     Sans ligne Espèces (une salle dont le rapport n'en annonce pas, 02/10),
     le report la CRÉE, constaté posé : la finalisation n'additionne que les
     lignes, et les 915 € comptés en sortaient en silence. La route accepte
     cette création (`closing_pos_id` + moyen, comme « Ajouter un moyen »). */
  const reporterComptage = (cents: number) => {
    if (!ligneEspeces) {
      void api.ecrire('PUT', `/api/v1/finance/closings/${api.closing.id}/lines`, {
        lines: [{ closing_pos_id: pos.id, payment_method: MOYEN_ESPECES, observed_cents: cents }],
      })
      return
    }
    setObserves((x) => ({ ...x, [ligneEspeces.id]: cents }))
    void api.ecrire('PUT', `/api/v1/finance/closings/${api.closing.id}/lines`, {
      lines: [{ id: ligneEspeces.id, payment_method: ligneEspeces.payment_method, observed_cents: cents }],
    })
  }
  /* Rien à reporter : aucune ligne Espèces et 0 € compté — l'arrêté et le
     comptage disent déjà la même chose (la règle du serveur). */
  const aReporter = (cents: number | null): cents is number => cents !== null && (ligneEspeces !== null || cents !== 0)

  return (
    <>
      {/* ═══════════ 1. le rapport, ce que vous avez compté, l'écart ═══════════ */}
      <section className="lc-panel fiw-pv">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><Scale size={17} /></span>
          <h2 className="lc-panel__title">{pos.point_of_sale_name}</h2>
          <span className="lc-panel__spacer" />
          <span className="lc-hint">{pos.location_name} · point de vente {rang} sur {total}</span>
        </div>
        <div className="lc-panel__body fiw-pv__corps">
          <BilanPointDeVente
            chiffres={chiffres}
            origine={origine}
            aUneSource={aUneSource}
            seuils={seuils}
            nbTickets={nbTickets}
            comptageForce={overrideActif}
            comptage={comptageDirect}
            onReporter={editable && aReporter(comptageDirect) ? () => reporterComptage(comptageDirect) : null}
            sansLigneEspeces={ligneEspeces === null}
            explications={explicationHorsNuit}
          />
        </div>
      </section>

      {/* ═══════════ l'Accueil : les ventes du guichet d'une autre nuit ═══════════ */}
      {casHorsNuit.length > 0 ? <GuichetHorsNuit api={api} chiffres={chiffres} figee={figee} /> : null}

      {/* ═══════════ l'Accueil : les entrées, la guestlist à recopier ═══════════ */}
      {pos.point_of_sale_kind === 'BILLETTERIE' ? <EntreesAccueil api={api} figee={figee} /> : null}

      {/* ═══════════ 2. compter : les espèces, et les tickets TPE ═══════════ */}
      <div className="fiw-saisie">
        <section className="lc-panel">
          <div className="lc-panel__head">
            <span className="fih-head__icon" aria-hidden="true"><Banknote size={17} /></span>
            <h2 className="lc-panel__title">Comptage des espèces</h2>
            <span className="lc-panel__spacer" />
            <span className="fiw-total">
              <span className="fi-kpi__label">Total compté</span>
              <span className="fi-money fi-money--fort fiw-total__val">{formatMoney(totalCompte)}</span>
            </span>
          </div>
          <div className="lc-panel__body">
            {overrideActif ? (
              <div className="fiw-forcage">
                <p>
                  Le total espèces a été saisi directement :{' '}
                  <span className="fi-money fi-money--fort">{formatMoney(pos.cash_total_override_cents)}</span>.
                  C’est lui qui fait foi, pas le comptage ci-dessous.
                </p>
                {pos.cash_total_override_reason ? (
                  <p className="fi-reserve">« {pos.cash_total_override_reason} »</p>
                ) : null}
                {editable ? (
                  <button className="lc-btn lc-btn--sm" onClick={() => void reprendreComptage()}>
                    Revenir au comptage par coupures
                  </button>
                ) : null}
              </div>
            ) : null}

            <div className="fiw-coupures">
              {coupures.map((c) => {
                const q = Number.parseInt(quantites[String(c)] ?? '', 10)
                const valide = Number.isSafeInteger(q) && q > 0
                return (
                  <label key={c} className="fiw-coupure">
                    <span className="fiw-coupure__valeur">{formatMoney(c)}</span>
                    <span className="fiw-coupure__fois" aria-hidden="true">×</span>
                    <input
                      className="lc-input fiw-cash__qte"
                      inputMode="numeric"
                      aria-label={`Nombre de ${formatMoney(c)}`}
                      disabled={!editable}
                      placeholder="0"
                      value={quantites[String(c)] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^\d]/g, '')
                        setQuantites((x) => ({ ...x, [String(c)]: v }))
                      }}
                    />
                    <span className={`fiw-coupure__total fi-money${valide ? '' : ' fi-money--faible'}`}>
                      {valide ? formatMoney(c * q) : '—'}
                    </span>
                  </label>
                )
              })}
            </div>

            <div className="fiw-pied">
              {editable && !overrideActif ? (
                <button className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { setDirectCents(totalCompte); setDirectOuvert(true) }}>
                  Saisir le total directement
                </button>
              ) : <span />}
              <span className="lc-hint">Coupures réglables dans « Règles ».</span>
            </div>
          </div>
        </section>

        <TicketsTpe api={api} pos={pos} terminaux={terminaux} editable={editable} srcCarte={srcCarte} onTotal={surTickets} />
      </div>

      {/* ═══════════ 3. le rapprochement ═══════════ */}
      <section className="lc-panel">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><Scale size={17} /></span>
          <h2 className="lc-panel__title">Rapprochement</h2>
        </div>
        <div className="lc-panel__body lc-panel__body--flush">
          {pos.lines.length === 0 ? (
            <div className="fi-empty">
              <div className="fi-empty__title">Aucun montant sur ce point de vente</div>
              <p>Ajoutez le moyen de paiement encaissé pour commencer le rapprochement.</p>
            </div>
          ) : (
            <div className="fi-tablewrap fi-tablewrap--nu">
              <table className="fi-table fi-table--chiffres fiw-rappro">
                <colgroup>
                  <col />
                  <col className="fi-col-montant" />
                  <col className="fiw-col-saisie" />
                  <col className="fi-col-montant" />
                  <col className="fi-col-montant" />
                  <col className="fiw-col-action" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Moyen de paiement</th>
                    <th scope="col" className="fi-money">Annoncé</th>
                    <th scope="col" className="fi-money">Constaté</th>
                    <th scope="col" className="fi-money">Écart</th>
                    <th scope="col" className="fi-money">Retenu</th>
                    <th scope="col"><span className="fic-sr">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {lignesComptees.map((l) => {
                    // L'attendu : la source corrigée des ventes d'une autre nuit décidées.
                    const src = enCentimes(attenduDe(l))
                    const transfert = enCentimes(l.transfer_cents ?? null) ?? 0
                    // Les tickets EN DIRECT : la ligne suit la frappe, comme les cases du haut.
                    const parTickets = l.payment_method === MOYEN_CARTE && (pos.tpe_tickets.length > 0 || ticketsDirect !== null)
                    const obs = parTickets ? ticketsDirect : observes[l.id] ?? null
                    const ecart = src === null || obs === null ? null : obs - src
                    return (
                      <tr key={l.id}>
                        <th scope="row">
                          <span className="fi-cell-titre">{l.payment_method_label}</span>
                          {parTickets ? <span className="fi-cell-sous">{nbTickets > 1 ? `somme des ${nbTickets} tickets TPE` : 'le ticket TPE'}</span> : null}
                          {l.payment_method === MOYEN_ESPECES ? <span className="fi-cell-sous">compté : {formatMoney(especesEffectives)}</span> : null}
                          {transfert !== 0 ? (
                            <span className="fi-cell-sous">
                              source {formatMoney(l.source_cents)} {transfert > 0 ? '+' : '−'} {formatMoney(Math.abs(transfert))} {transfert > 0 ? 'rattachés ici' : 'rattachés à une autre nuit'}
                            </span>
                          ) : null}
                        </th>
                        <td className="fi-money"><Montant cents={src} /></td>
                        <td className="fi-money">
                          {editable && !parTickets ? (
                            <MoneyInput
                              cents={obs}
                              onChange={(c) => setObserves((x) => ({ ...x, [l.id]: c }))}
                              onBlur={() => void enregistrerConstate(l)}
                              ariaLabel={`Montant constaté — ${l.payment_method_label}`}
                            />
                          ) : <Montant cents={obs} fort />}
                        </td>
                        <td className="fi-money"><Ecart cents={ecart} seuils={seuils} /></td>
                        <td className="fi-money">
                          <Montant cents={l.retained_cents} fort />
                          {l.override_reason ? <span className="fiw-rappro__motif" title={l.override_reason}>motivé</span> : null}
                        </td>
                        <td className="fic-cta">
                          {peutRetenir ? (
                            <button className="lc-btn lc-btn--ghost lc-btn--sm"
                              onClick={() => setRetenue({ ligne: l, cents: enCentimes(l.retained_cents) ?? obs, motif: l.override_reason ?? '' })}>
                              Retenir un montant
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })}
                  {lignesOffertes.map((l) => (
                    <tr key={l.id} className="fiw-rappro__info">
                      <th scope="row">
                        <span className="fi-cell-titre">{l.payment_method_label}</span>
                        <span className="fi-cell-sous">Carte Serveur — une information, rien à compter</span>
                      </th>
                      <td className="fi-money"><Montant cents={l.source_cents} faible /></td>
                      <td className="fi-money"><span className="fi-money--faible">—</span></td>
                      <td />
                      <td className="fi-money"><Montant cents={l.retained_cents} /></td>
                      <td />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="lc-panel__body">
          {/* Le report est PROPOSÉ, jamais fait dans le dos : reprendre le
              comptage sur la ligne Espèces est une décision. */}
          {editable && especesEffectives !== null && especesEffectives > 0
            && (ligneEspeces === null || especesEffectives !== enCentimes(ligneEspeces.observed_cents)) ? (
            <div className="fiw-report">
              {ligneEspeces ? (
                <span>
                  Le comptage donne <span className="fi-money fi-money--fort">{formatMoney(especesEffectives)}</span>,
                  la ligne Espèces porte <span className="fi-money">{formatMoney(ligneEspeces.observed_cents)}</span>.
                </span>
              ) : (
                <span>
                  Le comptage donne <span className="fi-money fi-money--fort">{formatMoney(especesEffectives)}</span>,
                  et aucune ligne Espèces ne les porte : le report la crée.
                </span>
              )}
              <button className="lc-btn lc-btn--sm" onClick={() => reporterComptage(especesEffectives)}>
                Reporter le comptage
              </button>
            </div>
          ) : null}

          {editable && moyensLibres.length > 0 ? (
            <details className="fiw-ajout">
              <summary>Ajouter un moyen de paiement</summary>
              <div className="fiw-ajout__ligne">
                <Select
                  value={nouveau}
                  options={moyensLibres.map((m) => ({ value: m.code, label: m.label }))}
                  onChange={setNouveau}
                  placeholder="Moyen de paiement"
                  ariaLabel="Moyen de paiement à ajouter"
                  compact
                />
                <button className="lc-btn" disabled={!nouveau} onClick={() => void ajouterLigne()}>
                  <Plus size={16} aria-hidden="true" />Ajouter
                </button>
              </div>
            </details>
          ) : null}
        </div>
      </section>

      {/* ═══════════ 4. le détail du point de vente (à lire si l'on veut) ═══════════ */}
      <PosDetail
        nuit={api.closing.business_date}
        pointOfSaleId={pos.point_of_sale_id}
        nom={pos.point_of_sale_name}
        kind={pos.point_of_sale_kind}
        peut={api.peut}
      />

      {/* ═══════════ saisie directe du total espèces ═══════════ */}
      <Modal open={directOuvert} onClose={() => setDirectOuvert(false)} ariaLabel="Saisir le total des espèces" width={520} owning>
        <header className="lc-modal__head">
          <span className="lc-modal__icon" aria-hidden="true"><Banknote size={22} /></span>
          <div>
            <div className="lc-modal__title">Total des espèces, sans détail</div>
            <div className="lc-modal__pills">
              <span className="lc-pill lc-pill--idle">{pos.point_of_sale_name}</span>
            </div>
          </div>
          <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={() => setDirectOuvert(false)} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>
        <div className="lc-modal__body fiw-modal">
          <p className="lc-hint">
            Ce total remplacera le comptage par coupures. Il court-circuite la
            seule vérification arithmétique disponible : c’est pourquoi il
            demande une explication.
          </p>
          <div className="lc-field">
            <span className="lc-label">Total compté à la main</span>
            <MoneyInput cents={directCents} onChange={setDirectCents} ariaLabel="Total des espèces" />
          </div>
          <MotifField
            id="fiw-motif-especes"
            valeur={directMotif}
            onChange={setDirectMotif}
            label="Pourquoi le détail n’est-il pas saisi ?"
          />
          <div className="fiw-modal__actions">
            <button className="lc-btn lc-btn--ghost" onClick={() => setDirectOuvert(false)}>Annuler</button>
            <button
              className="lc-btn lc-btn--primary"
              disabled={directCents === null || !motifSuffisant(directMotif)}
              onClick={() => void poserTotalDirect()}
            >Enregistrer ce total</button>
          </div>
        </div>
      </Modal>

      {/* ═══════════ retenir un autre montant ═══════════ */}
      <Modal open={retenue !== null} onClose={() => setRetenue(null)} ariaLabel="Retenir un autre montant" width={560} owning>
        {retenue ? (
          <>
            <header className="lc-modal__head">
              <span className="lc-modal__icon" aria-hidden="true"><Scale size={22} /></span>
              <div>
                <div className="lc-modal__title">Retenir un autre montant</div>
                <div className="lc-modal__pills">
                  <span className="lc-pill lc-pill--idle">{pos.point_of_sale_name}</span>
                  <span className="lc-pill lc-pill--idle">{retenue.ligne.payment_method_label}</span>
                </div>
              </div>
              <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={() => setRetenue(null)} aria-label="Fermer">
                <X size={18} />
              </button>
            </header>
            <div className="lc-modal__body fiw-modal">
              <div className="fi-triptyque">
                <div className="fi-triptyque__cell">
                  <div className="fi-triptyque__label">Source</div>
                  <div className="fi-triptyque__value">{formatMoney(retenue.ligne.source_cents)}</div>
                </div>
                <div className="fi-triptyque__cell">
                  <div className="fi-triptyque__label">Constaté</div>
                  <div className="fi-triptyque__value">{formatMoney(retenue.ligne.observed_cents)}</div>
                </div>
                <div className="fi-triptyque__cell fi-triptyque__cell--retenu">
                  <div className="fi-triptyque__label">Retenu</div>
                  <div className="fi-triptyque__value">{formatMoney(retenue.cents)}</div>
                </div>
              </div>

              <p className="lc-hint">
                La source ne disparaît jamais. Retenir un autre montant ajoute une
                décision par-dessus, sans effacer ce que le fournisseur a transmis.
              </p>

              <div className="lc-field">
                <span className="lc-label">Montant retenu</span>
                <MoneyInput
                  cents={retenue.cents}
                  onChange={(c) => setRetenue((r) => (r ? { ...r, cents: c } : r))}
                  ariaLabel="Montant retenu"
                />
              </div>

              {/* Le motif est TOUJOURS exigé : la route de retenue le revalide,
                  et le seuil visuel ne dispense jamais de l'explication. */}
              <MotifField
                id="fiw-motif-retenue"
                valeur={retenue.motif}
                onChange={(v) => setRetenue((r) => (r ? { ...r, motif: v } : r))}
                aide="Retenir un montant différent du constaté est une décision : elle se motive. Confirmer le constaté n’exige rien."
              />

              <div className="fiw-modal__actions">
                <button className="lc-btn lc-btn--ghost" onClick={() => setRetenue(null)}>Annuler</button>
                <button
                  className="lc-btn lc-btn--primary"
                  disabled={retenue.cents === null || !motifSuffisant(retenue.motif)}
                  onClick={() => void poserRetenue()}
                >Retenir ce montant</button>
              </div>
            </div>
          </>
        ) : null}
      </Modal>
    </>
  )
}

/* ═══════════════════════════ LES CASES DU HAUT ═══════════════════════════ */

/** Le nom d'une ligne des cases : Carte et Espèces avec leur pictogramme, les autres sous leur libellé. */
function nomMoyen(m: ChiffresMoyen): ReactNode {
  if (m.code === MOYEN_CARTE) return <><CreditCard size={15} aria-hidden="true" />Carte</>
  if (m.code === MOYEN_ESPECES) return <><Banknote size={15} aria-hidden="true" />Espèces</>
  return m.libelle ?? m.code
}

const NIVEAU: Record<Exclude<NiveauEcart, 'aucun'>, { pastille: string; texte: string }> = {
  ok: { pastille: 'lc-pill lc-pill--ok', texte: 'Dans la tolérance' },
  tiede: { pastille: 'lc-pill lc-pill--warning', texte: 'À surveiller' },
  fort: { pastille: 'lc-pill lc-pill--error', texte: 'À expliquer' },
}

/**
 * UN GROUPE DES CASES DU HAUT — un titre, des lignes, un total, un pied.
 *
 * Les trois groupes partagent les MÊMES rangées (grille parente, rangées en
 * `subgrid`, finance.css) : « Carte » du rapport, du compté et de l'écart
 * tombent sur la même ligne, quelle que soit la longueur d'une aide. D'où un
 * nombre d'enfants fixe : tête, une ligne par moyen, total, pied — le pied
 * est rendu même vide.
 */
function Groupe(props: {
  genre: 'rapport' | 'compte' | 'ecart'
  niveau?: NiveauEcart
  icone: ReactNode
  titre: string
  sous: string
  etiquette?: ReactNode
  children: ReactNode
}) {
  const id = useId()
  return (
    <div
      role="group"
      aria-labelledby={id}
      className={`fiw-groupe fiw-groupe--${props.genre}${props.niveau ? ` fiw-groupe--${props.niveau}` : ''}`}
    >
      <div className="fiw-groupe__tete">
        <span className="fiw-groupe__icone" aria-hidden="true">{props.icone}</span>
        <span className="fiw-groupe__titres">
          <h3 id={id} className="fiw-groupe__titre">{props.titre}</h3>
          <span className="fiw-groupe__sous">{props.sous}</span>
        </span>
        {props.etiquette ?? null}
      </div>
      {props.children}
    </div>
  )
}

function Ligne(props: { nom: ReactNode; aide?: ReactNode; total?: boolean; children: ReactNode }) {
  return (
    <div className={`fiw-groupe__ligne${props.total ? ' fiw-groupe__ligne--total' : ''}`}>
      <span className="fiw-groupe__nom">
        <span className="fiw-groupe__lib">{props.nom}</span>
        {props.aide ? <span className="fiw-groupe__aide">{props.aide}</span> : null}
      </span>
      <span className="fiw-groupe__val">{props.children}</span>
    </div>
  )
}

/**
 * CE QUE LE RAPPORT ANNONCE · CE QUE VOUS AVEZ COMPTÉ · L'ÉCART.
 *
 * Trois groupes côte à côte (empilés quand la colonne est étroite), aux
 * lignes alignées. Le compté et l'écart suivent la saisie en direct. Un
 * chiffre inconnu s'écrit « — », jamais 0 : un point de vente sans source
 * n'a pas « 0,00 € annoncé », un terminal pas encore saisi n'est pas un
 * manque.
 */
function BilanPointDeVente(props: {
  chiffres: ChiffresPointDeVente
  origine: string
  aUneSource: boolean
  seuils: { warn: number; alert: number }
  nbTickets: number
  /** Le total des espèces a été saisi directement (il fait foi sur les coupures). */
  comptageForce: boolean
  /** Le comptage à l'écran ; `null` = rien de compté. */
  comptage: number | null
  /** Reporter le comptage sur la ligne Espèces ; `null` = rien à proposer. */
  onReporter: (() => void) | null
  /** Le point de vente n'a pas de ligne Espèces : le report la créera. */
  sansLigneEspeces: boolean
  /** D'où vient l'écart quand une vente du guichet d'une autre nuit attend sa décision. */
  explications?: string[]
}) {
  const { chiffres: c, origine, seuils, nbTickets, comptageForce, comptage, onReporter } = props
  const niveau = niveauEcart(c.ecart, seuils)
  const e = formatEcart(c.ecart)
  const repartitionFausse: ReactNode = c.complet && c.ecart !== null && Math.abs(c.ecart) < seuils.alert
    && c.moyens.some((m) => m.ecart !== null && Math.abs(m.ecart) >= seuils.alert)
    ? c.moyens.filter((m) => m.ecart !== null && m.ecart !== 0).map((m, i) => (
      <span key={m.code}>
        {i > 0 ? ', ' : ''}{m.code === MOYEN_CARTE ? 'carte' : m.code === MOYEN_ESPECES ? 'espèces' : (m.libelle ?? m.code).toLowerCase()}{' '}
        <span className="fi-money">{formatEcart(m.ecart).texte}</span>
      </span>
    ))
    : null
  const nomComptage = comptageForce ? 'total saisi directement' : 'comptage par coupures'
  const rangs = c.moyens.length + 3

  const aideCompte = (m: ChiffresMoyen): ReactNode => {
    if (m.code === MOYEN_CARTE) {
      if (m.origine === 'tickets') return nbTickets > 1 ? `somme des ${nbTickets} tickets TPE` : 'le ticket TPE'
      return m.origine === 'saisie' ? 'saisi au rapprochement' : 'à saisir : un ticket par TPE'
    }
    if (m.code === MOYEN_ESPECES) {
      const reporter = onReporter ? (
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm fiw-groupe__geste" onClick={onReporter}>
          Reporter sur la ligne Espèces
        </button>
      ) : null
      if (m.origine === 'comptage') return <>{nomComptage}, pas encore reporté{reporter}</>
      if (m.origine === 'saisie' && comptage !== null && comptage !== m.constate) {
        return <>le comptage donne {formatMoney(comptage)} : à reporter{reporter}</>
      }
      if (m.origine === 'saisie') return comptage !== null ? nomComptage : 'saisi au rapprochement'
      return 'à compter, coupure par coupure'
    }
    return m.origine === null ? 'à saisir au rapprochement' : 'saisi au rapprochement'
  }

  return (
    <div className="fiw-bilan" style={{ ['--fiw-rangs' as string]: rangs }}>
      {/* ─── le rapport ─── */}
      <Groupe
        genre="rapport"
        icone={<FileText size={16} />}
        titre="Ce que le rapport annonce"
        sous={`D’après le ${origine}, jamais réécrit`}
      >
        {c.moyens.map((m) => (
          <Ligne key={m.code} nom={nomMoyen(m)}>
            <span className={m.annonce === null ? 'fi-money fi-money--faible' : 'fi-money'}>{formatMoney(m.annonce)}</span>
          </Ligne>
        ))}
        <Ligne nom="Encaissé annoncé" aide="hors offerts" total>
          <span className="fi-money">{formatMoney(c.annonce)}</span>
        </Ligne>
        <div className="fiw-groupe__pied">
          {c.offerts !== null ? (
            <span className="fiw-groupe__offerts">
              <Gift size={14} aria-hidden="true" />
              <span>
                Offerts · Carte Serveur : <span className="fi-money">{formatMoney(c.offerts)}</span>.
                Une information, hors total : rien à compter.
              </span>
            </span>
          ) : !props.aUneSource ? (
            <span>
              Aucune source ne porte de montant pour ce point de vente : ce qui sera saisi sera un constat de
              terrain, sans rien à confronter.
            </span>
          ) : c.transfert !== 0 && c.annonce !== null ? (
            <span className="fiw-groupe__transfert">
              {c.transfert > 0
                ? `Guichet ${formatMoney(c.source)} + ${formatMoney(c.transfert)} rattachés = attendu ${formatMoney(c.annonce)}.`
                : `Source ${formatMoney(c.source)} − ${formatMoney(-c.transfert)} rattachés à une autre nuit = attendu ${formatMoney(c.annonce)}.`}
            </span>
          ) : null}
        </div>
      </Groupe>

      {/* ─── ce que vous avez compté ─── */}
      <Groupe
        genre="compte"
        icone={<Calculator size={16} />}
        titre="Ce que vous avez compté"
        sous="Tickets TPE, comptage, rapprochement : suit la saisie"
        etiquette={c.constate === null ? null : c.partiel
          ? <span className="lc-pill lc-pill--warning">En partie</span>
          : c.complet ? <span className="lc-pill lc-pill--ok">Tout est compté</span> : null}
      >
        {c.moyens.map((m) => (
          <Ligne key={m.code} nom={nomMoyen(m)} aide={aideCompte(m)}>
            <span className={m.constate === null ? 'fi-money fi-money--faible' : 'fi-money'}>{formatMoney(m.constate)}</span>
          </Ligne>
        ))}
        <Ligne
          nom="Encaissé compté"
          aide={c.constate === null
            ? 'rien de compté pour l’instant'
            : c.partiel ? `${c.comptees} ligne${c.comptees > 1 ? 's' : ''} comptée${c.comptees > 1 ? 's' : ''} sur ${c.total}` : 'hors offerts'}
          total
        >
          <span className={c.constate === null ? 'fi-money fi-money--faible' : 'fi-money'}>{formatMoney(c.constate)}</span>
        </Ligne>
        <div className="fiw-groupe__pied">
          {/* Le rail, lui, lit le comptage dès qu'il est enregistré : seul le
              RETENU l'ignore — et la nuit ne se finalise pas sans le report.
              Sans ligne Espèces et à 0 € compté, il n'y a rien à reporter. */}
          {c.comptageNonReporte && !(props.sansLigneEspeces && comptage === 0) ? (
            <span className="fiw-groupe__alerte">
              {props.sansLigneEspeces
                ? 'Le comptage n’est pas encore reporté sur la ligne Espèces : ce point de vente n’en a pas, le report la crée. D’ici là, le montant retenu ne connaît pas ces espèces, et la nuit ne se finalise pas.'
                : 'Le comptage n’est pas encore reporté sur la ligne Espèces : le montant retenu ne le connaît pas encore, et la nuit ne se finalise pas sans ce report.'}
            </span>
          ) : null}
        </div>
      </Groupe>

      {/* ─── l'écart ─── */}
      <Groupe
        genre="ecart"
        niveau={niveau}
        icone={<Scale size={16} />}
        titre="Écart du point de vente"
        sous="Compté moins annoncé, hors offerts"
        etiquette={niveau === 'aucun' ? null : <span className={NIVEAU[niveau].pastille}>{NIVEAU[niveau].texte}</span>}
      >
        {c.moyens.map((m) => (
          <Ligne
            key={m.code}
            nom={nomMoyen(m)}
            aide={m.ecart !== null ? null : m.annonce === null ? 'rien d’annoncé à confronter' : 'pas encore compté'}
          >
            {m.ecart === null ? <span className="fi-money fi-money--faible">—</span> : <Ecart cents={m.ecart} seuils={seuils} />}
          </Ligne>
        ))}
        <Ligne
          nom="Écart"
          aide={c.ecart === null
            ? 'se calcule dès qu’une ligne annoncée est comptée'
            : c.complet ? 'sur tout le point de vente' : 'sur les lignes déjà comptées seulement'}
          total
        >
          <span className={`fiw-groupe__ecart fiw-groupe__ecart--${niveau}`}>{e.texte}</span>
        </Ligne>
        <div className="fiw-groupe__pied">
          {(props.explications ?? []).map((x) => <span key={x} className="fiw-groupe__alerte fgh-explication">{x}</span>)}
          {/* Un total juste ne rend pas la répartition juste : le 03/10, −1 € au
              total cachait TPE −1 786 € et espèces +1 785 €. On le dit. */}
          {repartitionFausse ? (
            <span className="fiw-groupe__alerte fgh-explication">
              Le total tombe juste, pas la répartition : {repartitionFausse}. Vérifiez les tickets TPE et le comptage des espèces.
            </span>
          ) : null}
          <span>
            Un écart n’est pas une faute. Au-delà de {formatMoney(seuils.alert)}, il s’explique à l’étape Écarts.
          </span>
        </div>
      </Groupe>
    </div>
  )
}

/* ═══════════════════════════ LES TICKETS TPE ═══════════════════════════ */

interface TicketBrouillon {
  cle: string
  terminal_id: string | null
  amount_cents: number | null
  operator_person_id: string | null
  operator_label: string
  /** L'opérateur se saisit en nom libre (aucun profil « Barman » ne convient). */
  libre: boolean
}

const AUTRE = '__autre__'
let operateursEnCache: Array<{ id: string; name: string; position: string }> | null = null

const brouillonDe = (t: TpeTicketView, i: number): TicketBrouillon => ({
  cle: t.id || `t${i}`, terminal_id: t.terminal_id, amount_cents: t.amount_cents,
  operator_person_id: t.operator_person_id, operator_label: t.operator_label ?? '',
  libre: t.operator_person_id === null,
})

/**
 * UN TICKET PAR TPE — le total que le terminal imprime en fin de service, le
 * terminal, et QUI l'a tenu. La somme devient le constaté « Carte » du point
 * de vente. Les profils « Barman » du SIRH sont proposés ; tant qu'il n'y en a
 * pas, un nom libre.
 */
function TicketsTpe(props: {
  api: ClosingApi
  pos: ClosingPosView
  terminaux: TerminalRow[]
  editable: boolean
  srcCarte: number | null
  /** La somme des tickets chiffrés à l'écran, à chaque frappe (`null` = aucun), et leur nombre. */
  onTotal: (cents: number | null, n: number) => void
}) {
  const { api, pos, terminaux, editable, srcCarte, onTotal } = props
  const [tickets, setTickets] = useState<TicketBrouillon[]>(() => pos.tpe_tickets.map(brouillonDe))
  const [operateurs, setOperateurs] = useState(operateursEnCache ?? [])
  const nouvelle = useRef(0)

  /* Les cases du haut et le rapprochement suivent la frappe : la somme
     remonte à chaque changement, enregistrée ou non. */
  useEffect(() => {
    const chiffres = tickets.filter((t) => t.amount_cents !== null)
    onTotal(chiffres.length === 0 ? null : chiffres.reduce((t, x) => t + (x.amount_cents ?? 0), 0), chiffres.length)
  }, [tickets, onTotal])

  useEffect(() => {
    if (operateursEnCache) return
    void lireJson<Array<{ id: string; name: string; position: string }>>('/api/v1/finance/closings/tpe-operators', (v) => {
      const o = typeof v === 'object' && v !== null ? (v as Record<string, unknown>)['operators'] : null
      return Array.isArray(o) ? (o as Array<{ id: string; name: string; position: string }>) : null
    }).then((r) => { if (r.etat === 'ok') { operateursEnCache = r.valeur; setOperateurs(r.valeur) } })
  }, [])

  const enregistrer = (liste: TicketBrouillon[]) => {
    if (!editable) return
    const utiles = liste.filter((t) => t.amount_cents !== null)
    void api.ecrire('PUT', `/api/v1/finance/closings/${api.closing.id}/tpe/${pos.id}`, {
      tickets: utiles.map((t) => ({
        terminal_id: t.terminal_id,
        amount_cents: t.amount_cents,
        operator_person_id: t.libre ? null : t.operator_person_id,
        operator_label: t.libre ? (t.operator_label.trim() || null) : null,
      })),
    })
  }
  // L'écriture part HORS du calcul d'état : un effet de bord dans un
  // `setState(fn)` peut s'exécuter deux fois.
  const changer = (cle: string, patch: Partial<TicketBrouillon>, sauver: boolean) => {
    const n = tickets.map((t) => (t.cle === cle ? { ...t, ...patch } : t))
    setTickets(n)
    if (sauver) enregistrer(n)
  }
  const retirer = (cle: string) => {
    const n = tickets.filter((t) => t.cle !== cle)
    setTickets(n)
    enregistrer(n)
  }
  const ajouter = () => {
    nouvelle.current += 1
    setTickets((l) => [...l, {
      cle: `nouveau-${nouvelle.current}`, terminal_id: null, amount_cents: null,
      operator_person_id: null, operator_label: '', libre: operateurs.length === 0,
    }])
  }

  const total = tickets.reduce((t, x) => t + (x.amount_cents ?? 0), 0)
  const itemsPersonnes = [
    ...operateurs.map((o) => ({ value: o.id, label: o.name, hint: o.position })),
    { value: AUTRE, label: 'Autre personne (nom libre)' },
  ]

  return (
    <section className="lc-panel">
      <div className="lc-panel__head">
        <span className="fih-head__icon" aria-hidden="true"><CreditCard size={17} /></span>
        <h2 className="lc-panel__title">Tickets TPE</h2>
        <span className="lc-panel__spacer" />
        <span className="fiw-total">
          <span className="fi-kpi__label">Total carte</span>
          <span className="fi-money fi-money--fort fiw-total__val">{formatMoney(total)}</span>
        </span>
      </div>
      <div className="lc-panel__body">
        {tickets.length === 0 ? (
          <p className="lc-hint fiw-tickets__vide">
            Un ticket par terminal : le total imprimé en fin de service, et qui l’a tenu. Leur somme devient le
            constaté carte{srcCarte !== null ? `, en face des ${formatMoney(srcCarte)} annoncés` : ''}.
          </p>
        ) : (
          <ul className="fiw-tickets">
            {tickets.map((t, i) => (
              <li key={t.cle} className="fiw-ticket">
                <span className="fiw-ticket__num">{i + 1}</span>
                <Combobox
                  value={t.terminal_id}
                  items={terminaux.filter((x) => x.active).map((x) => ({
                    value: x.id, label: x.name,
                    ...(x.preferred_point_of_sale_name ? { hint: `Habituellement ${x.preferred_point_of_sale_name}` } : {}),
                  }))}
                  onChange={(v) => changer(t.cle, { terminal_id: v }, true)}
                  placeholder="Terminal"
                  ariaLabel={`Terminal du ticket ${i + 1}`}
                  clearable
                  disabled={!editable}
                  className="fiw-ticket__tpe"
                />
                <MoneyInput
                  cents={t.amount_cents}
                  onChange={(c) => changer(t.cle, { amount_cents: c }, false)}
                  onBlur={() => enregistrer(tickets)}
                  ariaLabel={`Montant du ticket ${i + 1}`}
                  placeholder="Montant"
                  disabled={!editable}
                  className="fiw-ticket__montant"
                />
                {t.libre || operateurs.length === 0 ? (
                  <input
                    className="lc-input fiw-ticket__qui"
                    placeholder="Tenu par"
                    aria-label={`Qui a tenu le TPE du ticket ${i + 1}`}
                    maxLength={80}
                    disabled={!editable}
                    value={t.operator_label}
                    onChange={(e) => changer(t.cle, { operator_label: e.target.value }, false)}
                    onBlur={() => enregistrer(tickets)}
                  />
                ) : (
                  <Combobox
                    value={t.operator_person_id}
                    items={itemsPersonnes}
                    onChange={(v) => changer(t.cle, v === AUTRE ? { libre: true, operator_person_id: null } : { operator_person_id: v }, v !== AUTRE)}
                    placeholder="Tenu par"
                    ariaLabel={`Qui a tenu le TPE du ticket ${i + 1}`}
                    clearable
                    disabled={!editable}
                    className="fiw-ticket__qui"
                  />
                )}
                {editable ? (
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" onClick={() => retirer(t.cle)} aria-label={`Retirer le ticket ${i + 1}`}>
                    <Trash2 size={15} />
                  </button>
                ) : <span />}
              </li>
            ))}
          </ul>
        )}
        <div className="fiw-pied">
          {editable ? (
            <button type="button" className="lc-btn lc-btn--sm" onClick={ajouter}>
              <Plus size={15} aria-hidden="true" />Ajouter un ticket TPE
            </button>
          ) : <span />}
          {srcCarte !== null && tickets.some((t) => t.amount_cents !== null) ? (
            <span className="lc-hint">Annoncé {formatMoney(srcCarte)} · écart <Ecart cents={total - srcCarte} seuils={{ warn: api.settings.discrepancy_warn_cents, alert: api.settings.discrepancy_alert_cents }} /></span>
          ) : null}
        </div>
        <p className="fi-reserve fiw-tickets__note">
          Un terminal peut servir plusieurs caisses au cours d’une nuit : un écart entre une caisse et un TPE n’est
          pas une erreur en soi — le rapprochement carte se lit au point de vente, au lieu ou à la nuit.
        </p>
        {operateurs.length === 0 && tickets.length > 0 ? (
          <p className="lc-hint fiw-tickets__note">
            Aucun profil « Barman » dans le SIRH pour l’instant : le nom se saisit librement. Dès qu’un salarié
            aura ce poste, il sera proposé ici.
          </p>
        ) : null}
      </div>
    </section>
  )
}
